import axios from "axios";
import { store } from "../stores/store";
import { showNotification } from "../slices/AlertMessageSlice";
import { checkTokenExpiry, getToken } from "../token/token";
import getHeaders from "../domain/DomainHeader";
import Cookies from "js-cookie";

const DEFAULT_TENANT =
  process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || "superadmin";

function getTenantHeaders() {
  // Server/build time (no window)
  if (typeof window === "undefined") {
    return {
      "X-Tenant-Id": DEFAULT_TENANT,
    };
  }

  // Browser time
  const subdomain = window.location.hostname.split(".")[0];
  const customTenant = Cookies.get("custom_tenant");
  const hideTenantId = [""];

  if (customTenant) {
    return {
      "X-Tenant-Id": customTenant,
    };
  }

  if (subdomain === "www" || subdomain === "localhost") {
    return {
      "X-Tenant-Id": DEFAULT_TENANT,
    };
  }

  return {
    ...(!hideTenantId.includes(window.location.pathname) && {
      "X-Tenant-Id": subdomain,
    }),
  };
}

const APIFileUpload = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    "X-Tenant-Id": DEFAULT_TENANT,
  },
});

APIFileUpload.interceptors.request.use(
  async (config) => {
    console.log("APIFileUpload config", config);

    // Check token expiry
    checkTokenExpiry();

    // Always ensure tenant header exists (server+browser)
    config.headers = {
      ...(config.headers || {}),
      ...getTenantHeaders(),
    };

    // For FormData, never force Content-Type so axios/browser can attach boundary.
    if (config.data instanceof FormData) {
      if (typeof config.headers?.delete === "function") {
        config.headers.delete("Content-Type");
        config.headers.delete("content-type");
      } else {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }

    // Additional domain headers (browser only)
    if (typeof window !== "undefined") {
      const extra = await getHeaders(window.location.hostname, false);
      config.headers = {
        ...(config.headers || {}),
        ...extra,
      };

      if (config.data instanceof FormData) {
        if (typeof config.headers?.delete === "function") {
          config.headers.delete("Content-Type");
          config.headers.delete("content-type");
        } else {
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        }
      }
    }

    // Token (browser only usually)
    const token = typeof window !== "undefined" ? getToken("token") : null;
    if (token) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: "Bearer " + token,
      };
    }

    return config;
  },
  (error) => {
    console.log("Error in APIFileUpload request", error);
    return Promise.reject(error);
  },
);

APIFileUpload.interceptors.response.use(
  (response) => {
    console.log("api response", response);
    if (typeof window !== "undefined") {
      if (
        response.data.success &&
        (response.config.method === "post" ||
          response.config.method === "put" ||
          response.config.method === "delete")
      ) {
        if (response?.config?.url === "/cart") {
          store.dispatch(
            showNotification({
              messageToaster: true,
              toasterType: "success",
              toasterMessage: response?.data?.message || "Success!",
              action: true,
              actionPath: "/cart",
              actionLeble: "go to cart",
            }),
          );
        } else {
          store.dispatch(
            showNotification({
              messageToaster: true,
              toasterType: "success",
              toasterMessage: response?.data?.message || "Success!",
            }),
          );
        }
      }
    }
    return response;
  },
  (error) => {
    console.log("Error in api response", error);
    if (typeof window !== "undefined") {
      if (
        !error?.response?.data?.success &&
        (error.config?.method === "post" ||
          error.config?.method === "put" ||
          error.config?.method === "delete")
      ) {
        store.dispatch(
          showNotification({
            messageToaster: true,
            toasterType: "error",
            toasterMessage:
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              "Something went wrong! Please check your internet connection.",
          }),
        );
      } else if (error?.response?.data?.error === "Unauthorized") {
        store.dispatch(
          showNotification({
            messageToaster: true,
            toasterType: "error",
            toasterMessage: "Your session is expired! Please login",
            action: true,
            actionPath: "/user/login",
            actionLeble: "Login",
          }),
        );
      }
    }
    return Promise.reject(error);
  },
);

export default APIFileUpload;
